// Settings → Wise: whether Wise is connected here (the token is in the server's environment, never shown), which
// profile, and what the token can do: read only (history, matching, payments made in the Wise app, webhooks) or full
// access (creating transfers too) and how to switch; the two webhooks, exactly as to make them by hand in Wise (copy
// buttons), when each last sent a real and a test event; and the last events. Who may pay is Settings → Users → Can pay.
import { CheckCircle2, CircleAlert, CircleDashed, Copy, RefreshCw } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { WISE_WEBHOOK_VERSION, WISE_WEBHOOKS } from "../../../shared/bills.ts";
import { postJson } from "../lib/bills.ts";
import { ago } from "../lib/format.ts";
import { me } from "../lib/session.ts";
import { useRows } from "../lib/sync.ts";
import { Badge, Button } from "../ui/controls.tsx";
import { toast } from "../ui/toast.tsx";

type Seen = { at: string; version: string; outcome: string } | null;
type Status = {
  enabled: boolean;
  fake: boolean;
  env: "live" | "sandbox";
  missing: string[];
  profileFromEnv: boolean;
  webhookUrl: string;
  webhookKey: string;
  webhooks: { trigger: string; real: Seen; test: Seen }[];
  refused: { at: string; why: string } | null;
  events: { count: number; last: string | null };
};

/** The server's address, for Wise's trusted IPs on the token. */
const SERVER_IP = "64.227.111.149";
const KEY_SCRIPT = "ssh -t hhl-apps bash /opt/hhl/tools/hhl-ops-wise-key.sh";

const ACCESS = {
  full: { tone: "ok", label: "Full access", text: "It can read history and create transfers (quotes and transfers only: funding stays in the Wise app)." },
  read_only: {
    tone: "warn",
    label: "Read only",
    text: "It reads transfers, recipients and receipts, and webhooks work. It can't create transfers, so Pay with Wise is off: pay in the Wise app and record it.",
  },
  failed: { tone: "danger", label: "Not working", text: "Wise didn't accept the token, or no business profile was found. See the error." },
  unknown: { tone: "neutral", label: "Not checked", text: "Check the connection to see what the token can do." },
} as const;

const copy = (s: string) =>
  navigator.clipboard.writeText(s).then(
    () => toast.ok("Copied"),
    () => toast.error("Couldn't copy: select it instead"),
  );

function CopyButton({ value, label }: { value: string; label: string }) {
  return (
    <button type="button" onClick={() => copy(value)} className="shrink-0 rounded p-1 text-muted hover:bg-surface-2 hover:text-text" title={`Copy ${label}`}>
      <Copy className="size-3.5" />
    </button>
  );
}

/** One "type this into Wise" line: what Wise calls the box, the value, a copy button. */
function Entry({ label, value, hint }: { label: string; value: string; hint?: ReactNode }) {
  return (
    <>
      <dt className="pt-1 text-muted">{label}</dt>
      <dd className="flex min-w-0 items-start gap-1">
        <div className="min-w-0">
          <code className="rounded bg-surface-2 px-1.5 py-0.5 break-all text-text">{value}</code>
          {hint && <div className="mt-0.5 text-[12px] text-muted">{hint}</div>}
        </div>
        <CopyButton value={value} label={label.toLowerCase()} />
      </dd>
    </>
  );
}

function SeenLine({ what, seen }: { what: string; seen: Seen }) {
  return (
    <div className="flex min-w-0 gap-2">
      <span className="w-20 shrink-0 text-muted">{what}</span>
      {seen ? (
        <span className="min-w-0">
          {ago(seen.at)}
          {seen.version && <span className="text-muted"> · schema {seen.version}</span>}
          {seen.outcome && <span className="block truncate text-muted">{seen.outcome}</span>}
        </span>
      ) : (
        <span className="text-muted">None yet</span>
      )}
    </div>
  );
}

export function WiseSettingsPage() {
  const [status, setStatus] = useState<Status | null>(null);
  const [busy, setBusy] = useState(false);
  const conn = useRows("wise_connection").find((c) => c.id === 1) ?? null;
  const events = useRows("wise_event")
    .slice()
    .sort((a, b) => b.received_at.localeCompare(a.received_at))
    .slice(0, 8);
  const lastEvent = events[0]?.id;
  const users = useRows("app_user").filter((u) => u.can_pay && u.active);
  const admin = me()?.role === "admin";
  const load = useCallback(() => {
    fetch("/api/wise/status")
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => {});
  }, []);
  // Again whenever an event arrives (wise_event is synced), so a test sent from Wise shows up here by itself.
  // biome-ignore lint/correctness/useExhaustiveDependencies: lastEvent is the trigger
  useEffect(load, [load, lastEvent]);
  const check = async () => {
    setBusy(true);
    try {
      await postJson("/api/wise/check");
      toast.ok("Checked the connection");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const access = ACCESS[conn?.access ?? "unknown"];
  const url = status?.webhookUrl ?? "https://ops.apps.handheldlegend.com/webhooks/wise";
  const working = (status?.webhooks ?? []).filter((h) => h.real || h.test).length;
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-8">
        <p className="text-[13px] text-muted">
          HHL Ops pays vendors from Wise's US business profile, in USD: it makes the quote and the transfer (or a batch of them) with the vendor, PO and bill in
          the reference, and someone approves and funds it in the Wise app. It never funds or moves money itself. Webhooks mark bills paid and store Wise's
          receipts.
        </p>

        <section className="mt-5 rounded-lg border border-line bg-surface p-4">
          <div className="flex flex-wrap items-center gap-2">
            {status?.enabled ? <CheckCircle2 className="size-4 text-ok" /> : <CircleAlert className="size-4 text-warn" />}
            <span className="font-medium">
              {status ? (status.enabled ? (status.fake ? "The fake Wise (WISE_FAKE=1, dev only)" : "Connected") : "Not connected") : "…"}
            </span>
            {status && <Badge tone={status.env === "live" ? "accent" : "warn"}>{status.env === "live" ? "Live" : "Sandbox"}</Badge>}
            {conn && <Badge tone={access.tone}>{access.label}</Badge>}
            {admin && status?.enabled && (
              <Button size="sm" className="ml-auto" icon={<RefreshCw className="size-3.5" />} disabled={busy} onClick={check}>
                Check the connection
              </Button>
            )}
          </div>
          {status?.enabled && (
            <dl className="mt-3 grid grid-cols-[minmax(0,140px)_1fr] gap-x-3 gap-y-1 text-[13px]">
              <dt className="text-muted">Profile</dt>
              <dd>
                {conn?.profile_name || "—"} {conn?.profile_id ? <span className="font-mono text-muted">#{conn.profile_id}</span> : null}
                {status.profileFromEnv && <span className="text-muted"> (WISE_PROFILE_ID)</span>}
              </dd>
              <dt className="text-muted">What the token can do</dt>
              <dd>{access.text}</dd>
              <dt className="text-muted">Last checked</dt>
              <dd>{conn?.checked_at ? ago(conn.checked_at) : "Never"}</dd>
              {conn?.error && (
                <>
                  <dt className="text-muted">Error</dt>
                  <dd className="text-danger">{conn.error}</dd>
                </>
              )}
            </dl>
          )}
          <p className="mt-3 text-[12px] text-muted">
            How the token is told apart: anything can read profiles; HHL Ops then asks Wise for a $1 quote (no recipient, no transfer, no money moves). A
            full-access token gets one; a read-only token is refused (403).
          </p>
        </section>

        <section className="mt-4 rounded-lg border border-line bg-surface p-4 text-[13px]">
          <div className="font-medium">Read only or full access</div>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <div>
              <div className="text-ok">A read-only token can</div>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted">
                <li>Check the connection (here, or wise-check on the server)</li>
                <li>Import Wise history, from the API or a statement CSV, and confirm what each transfer paid</li>
                <li>
                  Record a payment made in the Wise app: Mark paid outside → Wise, with its transfer number. HHL Ops follows it and fetches Wise's receipt
                </li>
                <li>Take webhooks: statuses, bills paid, receipts. Without them it checks open transfers every 30 minutes</li>
                <li>Find vendors' recipients</li>
              </ul>
            </div>
            <div>
              <div className="text-warn">It can't</div>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted">
                <li>Pay with Wise from HHL Ops (quotes, transfers, batches): pay in the Wise app and record it</li>
                <li>Make webhooks with wise-subscribe: make them by hand in Wise (below)</li>
              </ul>
            </div>
          </div>
          <div className="mt-3 text-muted">
            <span className="text-text">To switch</span> (either way): make a new token in Wise with the other access (Your account → Connect and manage apps →
            API tokens → Add new token; trusted IP <code className="rounded bg-surface-2 px-1">{SERVER_IP}</code>
            <CopyButton value={SERVER_IP} label="the IP" />
            ), save it on the server with the same script, <code className="rounded bg-surface-2 px-1">{KEY_SCRIPT}</code>
            <CopyButton value={KEY_SCRIPT} label="the command" />, then Check the connection here. Webhooks stay as they are; revoke the old token in Wise.
          </div>
        </section>

        <section className="mt-4 rounded-lg border border-line bg-surface p-4 text-[13px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">Webhooks</span>
            {status && (
              <Badge tone={working === WISE_WEBHOOKS.length ? "ok" : working ? "warn" : "neutral"}>
                {working === WISE_WEBHOOKS.length ? "Both working" : working ? "One working" : "Nothing received yet"}
              </Badge>
            )}
          </div>
          <p className="mt-1 text-muted">
            Make these two in Wise, on the business account: Your account → Connect and manage apps (where API tokens are) → Webhooks → Create, one per event.
            Wise sends a test event when each is made; it shows below (a test is recorded, never applied to a payment). Each event is checked against Wise's{" "}
            {status?.env === "live" ? "live" : "sandbox"} key; anything unsigned is refused.
          </p>
          {status?.refused && (
            <p className="mt-2 text-warn">
              A delivery was refused {ago(status.refused.at)}: {status.refused.why}. A webhook made in {status.env === "live" ? "sandbox" : "live"} Wise is
              signed with the other key.
            </p>
          )}
          <div className="mt-3 flex flex-col gap-3">
            {WISE_WEBHOOKS.map((h) => {
              const seen = status?.webhooks.find((x) => x.trigger === h.trigger);
              const ok = !!(seen?.real || seen?.test);
              return (
                <div key={h.trigger} className="rounded-md border border-line p-3">
                  <div className="flex items-center gap-2">
                    {ok ? <CheckCircle2 className="size-4 text-ok" /> : <CircleDashed className="size-4 text-muted" />}
                    <span className="font-medium">{h.label}</span>
                    <span className="font-mono text-[12px] text-muted">{h.trigger}</span>
                  </div>
                  <p className="mt-0.5 text-[12px] text-muted">{h.does}</p>
                  <dl className="mt-2 grid grid-cols-[minmax(0,90px)_1fr] gap-x-3 gap-y-1.5">
                    <Entry label="Name" value={h.name} />
                    <Entry label="URL" value={url} />
                    <Entry label="Event" value={h.trigger} hint={`Wise's documentation calls it "${h.label}"`} />
                    <Entry label="Version" value={WISE_WEBHOOK_VERSION} hint="If it asks: the newest. Any version works." />
                  </dl>
                  <div className="mt-2 flex flex-col gap-0.5 border-t border-line pt-2 text-[12px]">
                    <SeenLine what="Last event" seen={seen?.real ?? null} />
                    <SeenLine what="Last test" seen={seen?.test ?? null} />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[12px] text-muted">
            With a full-access token they can be made from the server instead:{" "}
            <code className="rounded bg-surface-2 px-1">docker exec hhl-hhl-ops-1 node server/src/cli.ts wise-subscribe</code> (a read-only token prints these
            same settings).
          </p>
        </section>

        <section className="mt-4 rounded-lg border border-line bg-surface p-4 text-[13px]">
          <div className="font-medium">Setting it up</div>
          <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-muted">
            <li>
              In Wise (the business account): <span className="text-text">Your account → Connect and manage apps → API tokens → Add new token</span>, with
              trusted IP {SERVER_IP}. <span className="text-text">Read only</span> imports history, matches it and follows payments made in the Wise app;{" "}
              <span className="text-text">Full access</span> pays from HHL Ops too. Wise's payment approval rules don't work with API transfers: if the account
              has one, Wise refuses them.
            </li>
            <li>
              On the server: <code className="rounded bg-surface-2 px-1">{KEY_SCRIPT}</code> asks for the token (never on screen), saves{" "}
              <code className="rounded bg-surface-2 px-1">WISE_API_TOKEN</code> and <code className="rounded bg-surface-2 px-1">WISE_ENV</code> in
              /opt/hhl/hhl-ops.env and restarts HHL Ops. Then Check the connection here.
            </li>
            <li>The two webhooks above.</li>
            <li>
              Past payments: Bills & payments → Wise history → Import from Wise (or a statement CSV), then confirm what each paid. Or on the server{" "}
              <code className="rounded bg-surface-2 px-1">node server/src/cli.ts wise-history --since 2026-02-01 --dry-run</code>.
            </li>
            <li>
              Each vendor: its panel → Wise recipient → find it by name or the last digits of its account. Only the recipient's id, name and last 4 are kept.
            </li>
          </ol>
          {status && !status.enabled && status.missing.length > 0 && (
            <p className="mt-2 text-[12px] text-warn">Missing on the server: {status.missing.join(", ")}.</p>
          )}
        </section>

        <section className="mt-4 rounded-lg border border-line bg-surface p-4 text-[13px]">
          <div className="font-medium">Who can pay</div>
          <p className="mt-1 text-muted">
            {users.length ? users.map((u) => u.name || u.email).join(", ") : "Nobody yet"}. Paying, recording payments made outside, confirming Wise history and
            linking vendors' recipients take "Can pay" (Settings → Users). Only someone who can pay gives or takes it, never their own.
          </p>
        </section>

        <section className="mt-4 rounded-lg border border-line bg-surface p-4 text-[13px]">
          <div className="flex items-center gap-2">
            <span className="font-medium">Last webhooks</span>
            <span className="text-muted">
              {status?.events.count ?? 0} received{conn?.last_event_at ? `, the last ${ago(conn.last_event_at)}` : ""}
            </span>
          </div>
          {!events.length ? (
            <p className="mt-1 text-muted">None yet.</p>
          ) : (
            <ul className="mt-2 flex flex-col gap-1 text-[12px]">
              {events.map((e) => (
                <li key={e.id} className="flex gap-2">
                  <span className="w-28 shrink-0 text-muted">{ago(e.received_at)}</span>
                  <span className="w-44 shrink-0 truncate font-mono">
                    {e.event_type || "?"}
                    {e.test ? " (test)" : ""}
                  </span>
                  <span className="min-w-0 truncate">{e.outcome || "…"}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
